import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Popover,
  Chip,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import { ITheme } from '../../resource/interfaces';
import apis from '../../apis';

interface ThemeSelectorProps {
  selectedThemeId?: string;
  onChange: (themeId: string) => void;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  selectedThemeId,
  onChange,
}) => {
  const [themes, setThemes] = useState<ITheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverTheme, setPopoverTheme] = useState<ITheme | null>(null);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      const result = await apis.theme.getThemes();
      if (result.success) {
        setThemes(result.themes);
      }
    } catch (error) {
      console.error('Failed to load themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (themeId: string) => {
    onChange(themeId);
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, theme: ITheme) => {
    setPopoverAnchor(event.currentTarget);
    setPopoverTheme(theme);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverTheme(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading themes...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
        Template Theme
      </Typography>
      
             <Box sx={{ 
         display: 'flex', 
         flexDirection: { xs: 'column', md: 'row' }, 
         gap: 2,
         flexWrap: 'wrap'
       }}>
         {themes.map((theme) => (
           <Box 
             key={theme._id} 
             sx={{ 
               display: 'flex', 
               alignItems: 'center', 
               gap: 2,
               minWidth: { xs: '100%', md: 'auto' },
               flex: { xs: '1 1 100%', md: '0 0 auto' }
             }}
           >
             <Box
               onClick={() => handleThemeChange(theme._id || '')}
               onMouseEnter={(e) => handlePopoverOpen(e, theme)}
               onMouseLeave={handlePopoverClose}
               sx={{ 
                 display: 'flex', 
                 alignItems: 'center', 
                 gap: 2, 
                 cursor: 'pointer',
                 flex: 1,
                 p: 1,
                 borderRadius: 1,
                 border: '1px solid transparent',
                 transition: 'all 0.2s ease',
                 '&:hover': {
                   border: `1px solid ${theme.mainColor}20`,
                   backgroundColor: `${theme.mainColor}08`,
                 }
               }}
             >
               <Box
                 sx={{
                   position: 'relative',
                   width: 32,
                   height: 32,
                   backgroundColor: theme.mainColor,
                   borderRadius: '50%',
                   border: selectedThemeId === theme._id 
                     ? `3px solid ${theme.mainColor}` 
                     : '2px solid #fff',
                   boxShadow: selectedThemeId === theme._id
                     ? `0 0 0 3px ${theme.mainColor}40, 0 4px 12px ${theme.mainColor}60`
                     : '0 2px 4px rgba(0,0,0,0.1)',
                   transition: 'all 0.2s ease',
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   '&:hover': {
                     transform: 'scale(1.05)',
                     boxShadow: selectedThemeId === theme._id
                       ? `0 0 0 3px ${theme.mainColor}40, 0 6px 16px ${theme.mainColor}80`
                       : `0 4px 12px ${theme.mainColor}40`,
                   }
                 }}
               >
                 {selectedThemeId === theme._id && (
                   <Check 
                     sx={{ 
                       color: '#fff', 
                       fontSize: 18,
                       filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                     }} 
                   />
                 )}
               </Box>
               <Typography variant="body2" sx={{ color: theme.mainColor, fontWeight: 500 }}>
                 {theme.name}
               </Typography>
             </Box>
           </Box>
         ))}
       </Box>

      {/* Theme Preview Popover */}
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {popoverTheme && (
          <Box sx={{ p: 8, maxWidth: 300, background: popoverTheme.colorSet.background }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: popoverTheme.colorSet.text }}>
              {popoverTheme.name}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
              {popoverTheme.colorSet.gradientColors?.slice(0, 5).map((color, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: color,
                    borderRadius: '50%',
                    border: '1px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              <Chip 
                label="Primary" 
                size="small" 
                sx={{ backgroundColor: popoverTheme.colorSet.primary, color: '#fff' }} 
              />
              <Chip 
                label="Secondary" 
                size="small" 
                sx={{ backgroundColor: popoverTheme.colorSet.secondary, color: '#fff' }} 
              />
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};
